// frontend/src/pages/ListaBeneficiariosPage/ListaBeneficiariosPage.jsx
import React, { useState, useEffect } from 'react';
import { Typography, Table, Button, Select, Modal, Form, DatePicker, message, List, Card, Row, Col } from 'antd';
import { UserAddOutlined, DeleteOutlined } from '@ant-design/icons';
import * as milhasService from '../../services/milhasService';
import dayjs from 'dayjs';
import { getErrorMessage } from '../../utils/errorHandler';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';

const { Title, Text } = Typography;
const { Option } = Select;

const ListaBeneficiariosPage = () => {
    const [beneficiarios, setBeneficiarios] = useState([]);
    const [programasComLista, setProgramasComLista] = useState([]);
    const [passageiros, setPassageiros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [programaPortadorFiltro, setProgramaPortadorFiltro] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [messageApi, contextHolder] = message.useMessage();

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [portadoresRes, resBenef, passRes] = await Promise.all([
                milhasService.getPortadores(),
                milhasService.getBeneficiariosLista(programaPortadorFiltro),
                milhasService.getPassageiros(),
            ]);
            const portadores = portadoresRes.data || [];
            const programasPortador = [];
            for (const p of portadores) {
                const ppRes = await milhasService.getProgramasDoPortador(p.id);
                const pps = (ppRes.data || []).filter(
                    pp => pp.politica_reset_cpf === 'LISTA_BENEFICIARIOS'
                );
                programasPortador.push(...pps);
            }
            setProgramasComLista(programasPortador);
            setBeneficiarios(resBenef.data || []);
            setPassageiros(passRes.data || []);
        } catch (err) {
            messageApi.error(getErrorMessage(err, 'Erro ao carregar dados.'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [programaPortadorFiltro]);

    const handleAdd = () => {
        form.resetFields();
        form.setFieldsValue({ data_cadastro: dayjs() });
        setModalVisible(true);
    };

    const handleSubmitAdd = async () => {
        try {
            const values = await form.validateFields();
            await milhasService.addBeneficiarioLista({
                programa_portador: values.programa_portador,
                passageiro: values.passageiro,
                data_cadastro: values.data_cadastro.format('YYYY-MM-DD'),
            });
            messageApi.success('Beneficiário adicionado à lista.');
            setModalVisible(false);
            fetchData();
        } catch (err) {
            messageApi.error(getErrorMessage(err, 'Erro ao adicionar.'));
        }
    };

    const handleRemover = async (id) => {
        try {
            await milhasService.removerBeneficiarioLista(id);
            messageApi.success('Remoção registrada. O slot ficará em carência conforme a política do programa.');
            fetchData();
        } catch (err) {
            messageApi.error(getErrorMessage(err, 'Erro ao registrar remoção.'));
        }
    };

    const columns = [
        { title: 'Portador / Programa', key: 'display', render: (_, r) => r.programa_portador_display || `${r.programa_portador}` },
        { title: 'Beneficiário', dataIndex: 'passageiro_nome', key: 'nome' },
        { title: 'CPF', dataIndex: 'passageiro_cpf', key: 'cpf' },
        { title: 'Data Cadastro', dataIndex: 'data_cadastro', key: 'data_cadastro' },
        {
            title: 'Status',
            key: 'status',
            render: (_, r) => r.data_remocao ? (
                <Text type="warning">Em carência (removido em {r.data_remocao})</Text>
            ) : (
                <Text type="success">Ativo</Text>
            ),
        },
        {
            title: 'Ação',
            key: 'acao',
            render: (_, r) => !r.data_remocao ? (
                <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleRemover(r.id)}>
                    Registrar remoção
                </Button>
            ) : null,
        },
    ];

    return (
        <div style={{ padding: isMobile ? 16 : 24 }}>
            {contextHolder}
            <div style={{ marginBottom: 24 }}>
                <Title level={2} style={{ margin: 0 }}>Lista de Beneficiários (Azul)</Title>
                <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                    Gerencie a lista de beneficiários para programas com política de lista (ex: Azul Fidelidade).
                </Text>
            </div>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={10}>
                    <Select
                        placeholder="Filtrar por programa"
                        allowClear
                        style={{ width: isMobile ? '100%' : 280 }}
                        value={programaPortadorFiltro}
                        onChange={setProgramaPortadorFiltro}
                        size={isMobile ? 'large' : 'middle'}
                    >
                        {programasComLista.map(pp => (
                            <Option key={pp.id} value={pp.id}>
                                {pp.portador_nome} – {pp.programa_nome}
                            </Option>
                        ))}
                    </Select>
                </Col>
                <Col xs={24} sm={12} md={14} style={{ textAlign: isMobile ? 'left' : 'right' }}>
                    <Button type="primary" icon={<UserAddOutlined />} onClick={handleAdd} block={isMobile} size={isMobile ? 'large' : 'middle'}>
                        Adicionar Beneficiário
                    </Button>
                </Col>
            </Row>
            {isMobile ? (
                <List
                    dataSource={beneficiarios}
                    loading={loading}
                    rowKey="id"
                    locale={{
                        emptyText: (
                            <EmptyStateGuide
                                title={programasComLista.length === 0
                                    ? 'Nenhum programa com lista de beneficiários.'
                                    : 'Nenhum beneficiário na lista.'}
                                description={programasComLista.length === 0
                                    ? 'Ative um programa com política de lista (ex.: Azul) em um portador para gerenciar beneficiários.'
                                    : 'Cadastre beneficiários para controlar quem pode receber emissões neste programa.'}
                                actionLabel={programasComLista.length === 0 ? undefined : 'Adicionar beneficiário'}
                                onAction={programasComLista.length === 0 ? undefined : handleAdd}
                                icon={<UserAddOutlined />}
                            />
                        ),
                    }}
                    renderItem={(item) => (
                        <Card style={{ marginBottom: 16 }}>
                            <div style={{ marginBottom: 12 }}>
                                <Text strong style={{ fontSize: '1rem', display: 'block' }}>{item.passageiro_nome}</Text>
                                <Text type="secondary" style={{ fontSize: '0.85rem' }}>{item.programa_portador_display}</Text>
                            </div>
                            <Row gutter={[16, 8]}>
                                <Col span={24}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>CPF</Text>
                                    <div style={{ fontWeight: 500 }}>{item.passageiro_cpf}</div>
                                </Col>
                                <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Data Cadastro</Text>
                                    <div style={{ fontWeight: 500 }}>{item.data_cadastro}</div>
                                </Col>
                                <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
                                    <div>
                                        {item.data_remocao ? (
                                            <Text type="warning" style={{ fontSize: 13 }}>Em carência (removido em {item.data_remocao})</Text>
                                        ) : (
                                            <Text type="success" style={{ fontSize: 13 }}>Ativo</Text>
                                        )}
                                    </div>
                                </Col>
                                {!item.data_remocao && (
                                    <Col span={24} style={{ marginTop: 8 }}>
                                        <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleRemover(item.id)} style={{ padding: 0 }}>
                                            Registrar remoção
                                        </Button>
                                    </Col>
                                )}
                            </Row>
                        </Card>
                    )}
                />
            ) : (
                <Table
                    columns={columns}
                    dataSource={beneficiarios}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 15 }}
                    scroll={{ x: 700 }}
                    locale={{
                        emptyText: (
                            <EmptyStateGuide
                                title={programasComLista.length === 0
                                    ? 'Nenhum programa com lista de beneficiários.'
                                    : 'Nenhum beneficiário na lista.'}
                                description={programasComLista.length === 0
                                    ? 'Ative um programa com política de lista (ex.: Azul) em um portador para gerenciar beneficiários.'
                                    : 'Cadastre beneficiários para controlar quem pode receber emissões neste programa.'}
                                actionLabel={programasComLista.length === 0 ? undefined : 'Adicionar beneficiário'}
                                onAction={programasComLista.length === 0 ? undefined : handleAdd}
                                icon={<UserAddOutlined />}
                            />
                        ),
                    }}
                />
            )}
            <Modal
                title="Adicionar Beneficiário à Lista"
                open={modalVisible}
                onOk={handleSubmitAdd}
                onCancel={() => setModalVisible(false)}
                okText="Adicionar"
                width={isMobile ? '100%' : 520}
                style={isMobile ? { top: 0, margin: 0, maxWidth: '100%', paddingBottom: 0 } : {}}
                bodyStyle={isMobile ? { maxHeight: 'calc(100dvh - 120px)', overflowY: 'auto' } : {}}
            >
                <Form form={form} layout="vertical" size={isMobile ? 'large' : 'middle'}>
                    <Form.Item name="programa_portador" label="Programa" rules={[{ required: true }]}>
                        <Select placeholder="Selecione o programa">
                            {programasComLista.map(pp => (
                                <Option key={pp.id} value={pp.id}>{pp.portador_nome} – {pp.programa_nome}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="passageiro" label="Passageiro" rules={[{ required: true }]}>
                        <Select
                            placeholder="Selecione o passageiro"
                            showSearch
                            optionFilterProp="children"
                            filterOption={(i, o) => (o?.label ?? '').toLowerCase().includes(i.toLowerCase())}
                            options={passageiros.map(p => ({ value: p.id, label: `${p.nome_completo} (${p.cpf})` }))}
                        />
                    </Form.Item>
                    <Form.Item name="data_cadastro" label="Data de Cadastro" rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ListaBeneficiariosPage;
